import { Injectable, signal } from '@angular/core';
import { IClip } from 'src/app/interfaces/iclip';
import { ICallClip } from 'src/app/interfaces/icall-clip';
import { WebSpeechApiService } from './web-speech-api.service';
import { ControlService } from './control.service';
import { AppConstants } from 'src/app/app-constants';
import { CallService } from 'src/app/services/learner/call.service';
import { IVideoClip } from 'src/app/interfaces/ivideo-clip';
import { IConversational } from 'src/app/interfaces/iconversational';
import { correctTranscript } from 'src/app/utils/transcript-corrector';
import { correctedTranscript } from 'src/app/utils/transcript-corrector';
import { IConverseStatus } from 'src/app/interfaces/iconverse-status';
import { UserVideoService } from './user-video.service';
import { CallStoreService } from './call-store/call-store.service';
import { ImagePreloaderService } from 'src/app/services/learner/image-preloader/image-preloader.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { AssemblyService } from './assembly/assembly.service';

@Injectable({
  providedIn: 'root',
})
export class PlayerService {
  webSpeechApiService: WebSpeechApiService | undefined;
  controlService: ControlService | undefined;
  // time limits for advance type auto
  waitTimeNoSpeechToSendResponse = 1500; // milliseconds
  waitTimeUserInterruptAvatarSpeech = 4000; // milliseconds

  public showLoadingScreen: boolean = true;

  callClips: ICallClip[] = [];
  currentClip: IClip | null = null;
  practiceCallClips: IVideoClip[] = [];
  isLoopActive: boolean = false;

  currentClipIndex: number = 0;
  currentVideoClipIndex: number = 0;
  isViewChanged: boolean = false;
  clipFilename: string = '';
  clipStartTime: number = 0;
  clipStopTime: number = 0;

  loopClipFilename: string | null = null;
  loopClipStartTime: number = 0;
  loopClipStopTime: number = 0;

  public videoHTMLElement: HTMLVideoElement | null = null;
  public avatarName: string = '';
  public avatarSpeech = signal<string>('');
  public isAvatarListening: boolean = false;

  public shouldHangup: boolean = false;
  public isFirstLoadPracticeView: boolean = false;
  preResponseWasPlayed: boolean = false;

  lastUserMessageSent: string | null = null;
  userInterruptedAvatar: boolean = false;
  timeToInterruptAvatarPassed: boolean = false;
  startPlayClipsCount: number = 0;
  isWaitingConversationalResponse: boolean = false;

  // transcript
  public lastQuestionUuidSent: string | null = null;
  finalAnswer: string = '';
  finalQuestionUuid: string | null = null;
  chatHistory: string = '';

  isPlayingPreResponse: boolean = false;
  avatarOffsetHeight: number = 0;
  avatarOffsetWidth: number = 0;
  canPlayVideo: boolean = false;
  isBeginCallDisabled: boolean = true;

  answerSentDateTime: Date | null = null;
  conversationalInterval: any = null;
  conversationStatusCode: number = 0;
  shouldHandleResponse: IConversational | null = null;
  converseStatus: IConverseStatus | null = null;

  triggerIsLoop: boolean = false;
  triggerStart: number = -1;
  hasPreCallClips: boolean = false;
  initPracticeCallClipsLength: number = 0;
  triggerViewChange: boolean = false;

  constructor(
    private callService: CallService,
    private userVideoService: UserVideoService,
    private callStore: CallStoreService,
    private imagePreloaderService: ImagePreloaderService,
    private logger: LoggerService,
    private assemblyService: AssemblyService
  ) {}

  setWebSpeechApiService(webSpeechApiService: WebSpeechApiService): void {
    this.webSpeechApiService = webSpeechApiService;
  }

  setControlService(controlService: ControlService): void {
    this.controlService = controlService;
  }

  startPlayClips(): void {
    this.startPlayClipsCount++;
    this.setCurrentIndexClipData();
    console.log(
      'video html element in start play clips',
      this.videoHTMLElement
    );
    if (this.videoHTMLElement) {
      const _this = this;
      this.videoHTMLElement.addEventListener('canplay', function onCanPlay() {
        _this.isBeginCallDisabled = false;
        if (!this) {
          return;
        }
        this.removeEventListener('canplay', onCanPlay);
        _this.startPlayClipsCount = 0;
        _this.showLoadingScreen = false;

        // if the call is not paused, play the video
        setTimeout(() => {
          const isPaused = _this.callStore.getIsPaused();
          const isVisible =
            !_this.videoHTMLElement?.classList.contains('d-none');
          if (!isPaused && isVisible) {
            const VIEW_PRACTICE = AppConstants.VIEW_PRACTICE;
            _this.videoHTMLElement
              ?.play()
              .then(() => {
                const isPracticeView =
                  _this.callStore.getCurrentView() == VIEW_PRACTICE;
                const displayTour = _this.controlService?.displayTour;
                if (displayTour && isPracticeView) {
                  this.pause();
                }
              })
              .catch((error) => {
                const isPracticeView =
                  _this.callStore.getCurrentView() == VIEW_PRACTICE;
                const interactError = 'interact with the document first';
                const iosInteractionError = 'not allowed by the user agent';
                if (
                  isPracticeView &&
                  (error.message.includes(interactError) ||
                    error.message.includes(iosInteractionError))
                ) {
                  if (!_this.callStore.getIsPaused()) {
                    _this.controlService?.togglePause();
                    const clip = _this.currentClip;
                    _this.logger.warn(
                      '[CALL] player - Interact with the document first, toggle pause',
                      {
                        clip,
                      }
                    );
                  }
                  return;
                }
                _this.logger.error('[CALL] player - error', error);
                const displayTour = _this.controlService?.displayTour;
                if (displayTour && isPracticeView) {
                  _this.videoHTMLElement?.pause();
                }
              });
          }

          if (!isVisible) {
            _this.logger.warn(
              '[CALL] player - Video is not visible then pause it...',
              {
                video: _this.videoHTMLElement,
              }
            );
            this.pause();
          }
        }, 1);
      });
      this.setTimeUpdateListener();
    } else {
      if (this.startPlayClipsCount < 20) {
        setTimeout(() => {
          console.log(`videoHTMLElement is null trying again...`);
          this.startPlayClips();
        }, 200);
      }
    }
  }

  get nextVisualAidIndex(): number | undefined | null {
    return this.currentClipIndex + 1 < this.callClips.length
      ? this.callClips[this.currentClipIndex + 1]?.visualAidIndex
      : undefined;
  }

  get previousVisualAidIndex(): number | undefined | null {
    return this.currentClipIndex > 0
      ? this.callClips[this.currentClipIndex - 1]?.visualAidIndex
      : undefined;
  }

  resetVideoHTMLElement(): void {
    if (this.videoHTMLElement) {
      this.videoHTMLElement.removeEventListener(
        'timeupdate',
        this.timeUpdateListener.bind(this)
      );
      this.videoHTMLElement.pause();
      this.videoHTMLElement = null;
    }
  }

  setTimeUpdateListener(): void {
    if (this.videoHTMLElement) {
      this.videoHTMLElement.addEventListener(
        'timeupdate',
        this.timeUpdateListener.bind(this)
      );
    }
  }

  timeUpdateListener(): void {
    this.onLoopInactive();
    this.onLoopActive();
    this.handleAutoAdvance();
  }

  handleAutoAdvance(): void {
    // advance type auto
    if (
      this.callStore.getAdvanceType() == AppConstants.ADVANCE_TYPE_AUTOMATIC
    ) {
      // if loop active
      if (this.isLoopActive) {
        this.handleUserStopSpeaking();
      }
      // if loop inactive and user interrupted avatar
      if (!this.isLoopActive) {
        this.handleTimeWindowToInterruptAvatarSpeech();
      }
    }
  }

  endCallAuto(): void {
    const isConversational = this.controlService?.isConversational();
    if (isConversational) {
      return;
    }

    this.maintainAgentChatHistory(0);
    this.controlService?.endCall();
    if (this.controlService) {
      this.callStore.setDisplayPostCall(true);
      this.assemblyService.stop();
    }
    if (this.videoHTMLElement) {
      this.videoHTMLElement.pause();
    }
  }

  endVisualAidCallAuto(): void {
    this.maintainAgentChatHistory(0);
    this.controlService?.endCallVisualAid();
    if (this.controlService) {
      this.callStore.setDisplayPostCall(true);
      this.assemblyService.stop();
    }
    if (this.videoHTMLElement) {
      this.videoHTMLElement.pause();
    }
  }

  onLoopInactive(): void {
    // loop inactive
    if (!this.isLoopActive && this.videoHTMLElement) {
      const isCurrentTimeGreaterThanClipStopTime =
        this.videoHTMLElement.currentTime >= this.clipStopTime;

      if (isCurrentTimeGreaterThanClipStopTime) {
        // pause video to prevent video keep playing while the next clip is loading
        this.videoHTMLElement.pause();
        // call complete redirect to post call, when it not conversational call
        const isPracticeView =
          this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE;
        const isConversational = this.controlService?.isConversational();
        if (
          this.callStore.getIsCallComplete() &&
          isPracticeView &&
          !isConversational
        ) {
          this.endCallAuto();
          return;
        }
        // if loop clip exists, play it
        if (this.loopClipFilename) {
          this.callStore.setIsVisualAidOpen(true);
          console.log(`PLAY VIDEO: onLoopInactive! `);
          if (this.callStore.getSkipConnectTranscriber()) {
            this.callStore.setSkipConnectTranscriber(false);
          } else {
            this.assemblyService.connectTranscriber(true);
          }
          this.timeToInterruptAvatarPassed = false;
          this.handlePlayVideo(
            this.loopClipFilename,
            this.loopClipStartTime,
            true
          );
          this.isLoopActive = true;
          // required for manual advance type
          if (this.isPlayingPreResponse) {
            this.isPlayingPreResponse = false;
            this.handleSendUserResponse();
          }
        }
        // no loop clip, go to next clip
        if (!this.loopClipFilename) {
          this.nextClip();
        }
      }
    }
  }

  onLoopActive(): void {
    // loop active
    if (this.isLoopActive && this.videoHTMLElement) {
      const isCurrentTimeGreaterThanLoopStopTime =
        this.videoHTMLElement.currentTime >= this.loopClipStopTime;
      if (this.loopClipFilename && isCurrentTimeGreaterThanLoopStopTime) {
        this.videoHTMLElement.currentTime = this.loopClipStartTime;
      }
    }
  }

  setCurrentIndexClipData(): void {
    const clip: ICallClip = this.callClips[this.currentClipIndex];
    const currentVisualIndex: number | undefined | null =
      this.callClips[this.currentClipIndex].visualAidIndex;
    console.log('clip: ', clip);
    if (clip) {
      this.handleClipViewSwitch(clip);
      const image = this.imagePreloaderService.getImageSrcByIndex(
        currentVisualIndex || 0
      );
      if (image) {
        console.log('Set visual aid image URL:', image);
        this.callStore.setVisualAidImageUrl(image);
      }
      // clip
      this.currentClip = clip.clip;
      this.handleCallClip();

      // clip loop
      const callClipLoop: IClip | null = clip.clipLoop;
      this.handleCallClipLoop(callClipLoop);

      if (this.clipFilename.length > 0 && !this.userInterruptedAvatar) {
        this.playClip();
      } else {
        this.playLoopClip();
      }

      this.setClipAvatarData(clip);
    }
  }

  setVideoSize(): void {
    // fn only required on visual aid call - TODO: delete when not needed
    if (
      this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE &&
      this.controlService?.isVisualAidCall &&
      !this.controlService.realtimeService
    ) {
      const isSmall =
        this.avatarOffsetHeight === 150 && this.avatarOffsetWidth === 300;
      const isZero =
        this.avatarOffsetHeight <= 0 || this.avatarOffsetWidth <= 0;
      if (isSmall || isZero) {
        const index = this.currentVideoClipIndex || 0;
        const id = `avatar-video-${index}`;
        const video = document.getElementById(id) as HTMLVideoElement;
        if (video) {
          this.removeClassFromClip('d-none', video.src);
          this.getVideoHeight();
        } else {
          console.log(`setVideoSize() - video is null, retry in 100ms`);
          setTimeout(() => {
            this.setVideoSize();
          }, 300);
        }
      }
    }
  }

  playClip(): void {
    this.handlePlayVideo(this.clipFilename, this.clipStartTime, false);
  }

  playLoopClip(): void {
    console.log('PLAY VIDEO: playLoopClip!');
    if (this.loopClipFilename) {
      this.handlePlayVideo(this.loopClipFilename, this.loopClipStartTime, true);
    }
  }

  retryPlayVideo(src: string, start: number, isLoop: boolean): void {
    setTimeout(() => {
      this.handlePlayVideo(src, start, isLoop);
    }, 110);
    this.isViewChanged = false;
  }

  handlePlayVideo(
    src: string,
    start: number,
    isLoop: boolean,
    isThinking: boolean = false
  ): void {
    // if view changed we wait few ms and try again when component is ready
    if (this.isViewChanged) {
      console.log('isViewChanges: ', this.isViewChanged);
      this.retryPlayVideo(src, start, isLoop);
      return;
    }

    // set avatar states
    this.callStore.setIsAvatarSpeaking(false);
    if (isLoop && (!this.isPlayingPreResponse || !isThinking)) {
      this.isAvatarListening = true;
      this.callStore.setIsAvatarThinking(false);
    }
    if (isThinking || this.isPlayingPreResponse) {
      this.isAvatarListening = false;
      this.callStore.setIsAvatarThinking(true);
    }
    if (
      !this.isLoopActive &&
      !this.isAvatarListening &&
      !this.callStore.getIsAvatarThinking()
    ) {
      this.callStore.setIsAvatarSpeaking(true);
    }
    if (this.callStore.getIsSendingAnswer()) {
      this.isAvatarListening = false;
      this.callStore.setIsAvatarThinking(true);
      this.callStore.setIsAvatarSpeaking(false);
    }

    const isDifferentVideo = this.videoHTMLElement?.src != src;
    // handle video switch to prevent video flashing
    if (!this.videoHTMLElement || isDifferentVideo) {
      // search for current video clip by src
      const current: IVideoClip | undefined = this.practiceCallClips.find(
        (obj) => obj.src === src
      );
      if (!current) {
        throw new Error(`current video is null on src: ${src}`);
      }
      this.currentVideoClipIndex = current.index;

      this.setVideoHTML();
      this.isLoopActive = false;
      if (this.videoHTMLElement) {
        this.checkCanPlay(start, isLoop);
      } else {
        // if videoHTMLElement is null we try again
        this.retryPlayVideo(src, start, isLoop);
      }
    } else {
      this.videoHTMLElement.pause();
      this.isLoopActive = isLoop;
      this.videoHTMLElement.currentTime = start;
      const handleSeeked = () => {
        this.videoHTMLElement!.removeEventListener('seeked', handleSeeked);

        this.videoHTMLElement
          ?.play()
          .then(() => {
            this.statusOnPlayVideo(isLoop);
            const displayTour = this.controlService?.displayTour;
            const isPracticeView =
              this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE;
            if (displayTour && isPracticeView) {
              setTimeout(() => {
                this.videoHTMLElement?.pause();
              }, 50);
            }
          })
          .catch((error) => {
            const isPracticeView =
              this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE;
            const interactError = 'interact with the document first';
            const iosInteractionError = 'not allowed by the user agent';
            if (
              isPracticeView &&
              (error.message.includes(interactError) ||
                error.message.includes(iosInteractionError))
            ) {
              if (!this.callStore.getIsPaused()) {
                this.logger.warn(
                  '[CALL] player - Interact with the document first, toggle pause',
                  {
                    video: this.videoHTMLElement,
                  }
                );
                this.controlService?.togglePause();
              }
              return;
            }
            this.logger.error('[CALL] player - error', error);
            const displayTour = this.controlService?.displayTour;
            if (displayTour && isPracticeView) {
              this.videoHTMLElement?.pause();
            }
          });
      };

      this.videoHTMLElement.addEventListener('seeked', handleSeeked);
    }
  }

  checkCanPlay(start: number, isLoop: boolean): void {
    if (this.videoHTMLElement) {
      this.videoHTMLElement.currentTime = start;
      this.triggerIsLoop = isLoop;
      this.triggerStart = start;
      if (this.videoHTMLElement.classList.contains('canplay')) {
        this.triggerCanPlay(isLoop, start);
      } else {
        this.addClassToClip('triggerCanPlay', this.videoHTMLElement.src);
        if (this.videoHTMLElement.readyState == 4) {
          this.triggerCanPlay(isLoop, start);
        }
      }
    }
  }

  triggerCanPlay(isLoop: boolean, start: number): void {
    if (this.videoHTMLElement) {
      this.isBeginCallDisabled = false;
      this.pauseAllVideos();
      if (this.loopClipFilename) {
        this.hideAllAvatarVideos(this.loopClipFilename);
      }
      // set timeupdate listener if not exists
      if (!this.videoHTMLElement.hasOwnProperty('timeupdate')) {
        this.setTimeUpdateListener();
      }

      const isPaused = this.callStore.getIsPaused();
      if (!isPaused && this.videoHTMLElement) {
        this.videoHTMLElement.currentTime = start;

        const handleSeeked = () => {
          this.videoHTMLElement!.removeEventListener('seeked', handleSeeked);

          this.videoHTMLElement!.play()
            .then(() => {
              this.statusOnPlayVideo(isLoop);
              const displayTour = this.controlService?.displayTour;
              const isPracticeView =
                this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE;
              if (displayTour && isPracticeView) {
                setTimeout(() => {
                  this.videoHTMLElement?.pause();
                }, 50);
              }
              const clip = this.currentClip;
              this.logger.log('[CALL] player - play', {
                clip,
              });
            })
            .catch((error) => {
              const clip = this.currentClip;
              this.logger.error(
                '[CALL] player error playing the avatar video',
                error?.message,
                {
                  clip,
                  error,
                }
              );
            });
        };

        this.videoHTMLElement.addEventListener('seeked', handleSeeked);
        const isPrecallView =
          this.callStore.getCurrentView() == AppConstants.VIEW_PRE_CALL;
        if (isPrecallView && this.videoHTMLElement) {
          this.removeClassFromClip('d-none', this.videoHTMLElement.src);
        }
      }
    }
  }

  statusOnPlayVideo(isLoop: boolean): void {
    if (!isLoop) {
      console.log(`Status on play video!`);
      this.isAvatarListening = false;
      if (this.isPlayingPreResponse) {
        this.callStore.setIsAvatarThinking(true);
        this.callStore.setIsAvatarSpeaking(false);
        this.preResponseWasPlayed = true;
      } else {
        this.callStore.setIsAvatarSpeaking(true);
        this.callStore.setIsAvatarThinking(false);
      }
    }
  }

  // handle to play the videos in case of view switch, to play the html video of the new displayed component
  async handleClipViewSwitch(clip: ICallClip): Promise<void> {
    const clipView = clip.type.toUpperCase();
    // if we have a view change from the clip then...
    if (clipView != this.callStore.getCurrentView() || this.triggerViewChange) {
      this.triggerViewChange = false;
      // update the current view to the new clip view
      this.callStore.setCurrentView(clipView);
      // handle first load of practice view
      if (this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE) {
        this.isFirstLoadPracticeView = true;
        this.hideAllAvatarVideos();
        this.resetVideoHTMLElement();
        // If cert acknowledgement is pending, pause before starting timer so avatar doesn't speak
        if (this.callStore.getCertAcknowledgementPending()()) {
          this.callStore.setIsPaused(true);
        } else {
          this.controlService?.startTimer();
        }
        setTimeout(() => {
          this.startPlayClips();
          // get first clip
          const isFirstVideoEmpty = this.clipFilename.trim().length === 0;
          this.assemblyService.restartTranscriber(isFirstVideoEmpty);
        }, 100);
        this.isViewChanged = true;
      }
    }
  }

  handleCallClip(): void {
    if (this.currentClip && this.isClipValid(this.currentClip)) {
      this.clipFilename = this.currentClip.filename;
      this.clipStartTime = this.convertTimeStringToSeconds(
        this.currentClip.start
      );
      this.clipStopTime = this.convertTimeStringToSeconds(
        this.currentClip.stop
      );
      // handle avatar speech on agentSays
      this.avatarSpeech.set(this.currentClip.agentSays);
    } else {
      // Handle the case when clip is null
      this.clipFilename = '';
      this.clipStartTime = 0;
      this.clipStopTime = 0;
      this.avatarSpeech.set('');
    }
  }

  playPreResponseCallClip(callPreResponseClip: IClip | null): void {
    if (callPreResponseClip) {
      // TODO: handle multiple
      const clip = callPreResponseClip;
      this.clipFilename = clip.filename;
      this.clipStartTime = this.convertTimeStringToSeconds(clip.start);
      this.clipStopTime = this.convertTimeStringToSeconds(clip.stop);
      this.avatarSpeech.set(clip.agentSays);
      this.handlePlayVideo(this.clipFilename, this.clipStartTime, false, true);
    }
  }

  handleCallClipLoop(callClipLoop: IClip | null): void {
    if (callClipLoop) {
      this.loopClipFilename = callClipLoop.filename;
      this.loopClipStartTime = this.convertTimeStringToSeconds(
        callClipLoop.start
      );
      this.loopClipStopTime = this.convertTimeStringToSeconds(
        callClipLoop.stop
      );
      this.loadVideoCurrentTime('video-background-loop');
    } else {
      // Handle the case when clip is null
      this.loopClipFilename = null;
      this.loopClipStartTime = 0;
      this.loopClipStopTime = 0;
    }
  }

  setClipAvatarData(clip: ICallClip): void {
    this.callStore.setHints(clip.hints);
    this.avatarName = clip.avatarName;
  }

  convertTimeStringToSeconds(timeString: string): number {
    let milliseconds = timeString.includes('.')
      ? parseInt(timeString.split('.').pop()!, 10) / 1000
      : 0;
    let parts = timeString.split(':'),
      seconds = 0,
      multiplier = 1;

    while (parts.length > 0) {
      seconds += multiplier * parseInt(parts.pop()!, 10);
      multiplier *= 60;
    }
    const fixedSeconds = (seconds + milliseconds).toFixed(3);
    return parseFloat(fixedSeconds);
  }

  nextClip(): void {
    this.logger.log('[CALL] player - NEXT CLIP!');

    // validate if we have internet connection
    const isPracticeView =
      this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE;
    if (!navigator.onLine && isPracticeView) {
      this.callStore.setOpenNoResponseModal(true);
      this.logger.error('[CALL] player - No internet connection!');
      return;
    }

    if (this.playPreResponseClip()) {
      this.logger.log('[CALL] player - Play PreResponse...');
      return;
    }

    this.handleSendUserResponse();
    this.currentClipIndex++;
    if (this.currentClipIndex < this.callClips.length) {
      this.setCurrentIndexClipData();
    } else {
      if (this.videoHTMLElement) {
        this.videoHTMLElement.pause();
      }
    }

    if (this.isFirstLoadPracticeView) {
      this.isFirstLoadPracticeView = false;
    }

    this.handleHighlightHangup();

    if (this.currentClipIndex >= this.callClips.length) {
      this.endCallAuto();
    }
  }

  async nextClipVisualAid(): Promise<void> {
    const answer = this.getAnswer();
    this.logger.log('[CALL] player - NEXT CLIP VISUAL AID');

    const isPracticeView =
      this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE;
    if (!navigator.onLine && isPracticeView) {
      this.callStore.setOpenNoResponseModal(true);
      this.logger.error('[CALL] player - No internet connection!');
      return;
    }

    if (this.playPreResponseClip()) {
      this.logger.log('[CALL] player - Visual aid Play PreResponse...');
      return;
    }

    // ✅ Wait for sendVisualAidResponse to finish
    await this.sendVisualAidResponse(answer);

    this.currentClipIndex++;
    if (this.currentClipIndex < this.callClips.length) {
      this.setCurrentIndexClipData();
    } else {
      if (this.videoHTMLElement) {
        this.videoHTMLElement.pause();
      }
    }

    if (this.isFirstLoadPracticeView) {
      this.isFirstLoadPracticeView = false;
    }

    this.handleHighlightHangup();

    if (this.currentClipIndex >= this.callClips.length) {
      this.logger.log('[CALL] player - End Call Auto');
      this.endVisualAidCallAuto();
    }
  }

  async nextClipVisualAidV2(): Promise<void> {
    const currentClipIndex = this.currentClipIndex;

    this.currentClipIndex++;
    if (this.currentClipIndex < this.callClips.length) {
      this.setCurrentIndexClipData();
    } else {
      if (this.videoHTMLElement) {
        this.videoHTMLElement.pause();
      }
    }

    await new Promise<void>((resolve) => setTimeout(resolve, 2500));
    const answer = this.getAnswer();
    console.log(`\n NEXT CLIP VISUAL AID! \n\n`);

    const isPracticeView =
      this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE;
    if (!navigator.onLine && isPracticeView) {
      this.callStore.setOpenNoResponseModal(true);
      this.logger.error('[CALL] player - No internet connection!');
      return;
    }

    if (this.playPreResponseClip()) {
      console.log(`Play PreResponse...`);
      return;
    }

    // ✅ Wait for sendVisualAidResponse to finish
    await this.sendVisualAidResponse(answer, currentClipIndex);

    if (this.isFirstLoadPracticeView) {
      this.isFirstLoadPracticeView = false;
    }

    this.handleHighlightHangup();

    if (this.currentClipIndex >= this.callClips.length) {
      console.log('End Call Auto');
      this.endVisualAidCallAuto();
    }
  }

  playPreResponseClip(): boolean {
    // Play PreResponse if conversational and practice
    const isPracticeView =
      this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE;
    const isConversational = this.controlService?.isConversational();
    const haveListenLoop =
      this.loopClipFilename != null && this.loopClipFilename != '';
    const preResponseClips =
      this.controlService?.callConversational?.preResponseClips;
    if (isPracticeView && isConversational) {
      if (haveListenLoop && preResponseClips && preResponseClips.length != 0) {
        const haveTranscript =
          this.callStore.getSpeechTranscript().length != 0 ||
          this.callStore.getSpeechTranscriptPartial().length != 0;
        if (haveTranscript && !this.isPlayingPreResponse) {
          this.callStore.setIsContinueButtonBlocked(true);
          const length = preResponseClips?.length || 0;
          const randomPreResponseCallClipIndex = Math.floor(
            Math.random() * length
          );
          if (preResponseClips && preResponseClips.length > 0) {
            this.playPreResponseCallClip(
              preResponseClips[randomPreResponseCallClipIndex]
            );
            this.isPlayingPreResponse = true;
          }
        }
        return true;
      }
    }
    return false;
  }

  beginCallFromPreCall(): void {
    // find first clip from practice screen
    while (this.currentClipIndex < this.callClips.length) {
      const clip = this.callClips[this.currentClipIndex];
      if (clip.type == AppConstants.VIEW_PRACTICE) {
        break;
      }
      this.currentClipIndex++;
    }

    // minus 1, because next clip adds 1
    this.currentClipIndex--;
    this.nextClip();
  }

  getNextClipSpeechTime(): number {
    const index = this.currentClipIndex + 1;
    if (this.callClips.length <= index) {
      return 0;
    }
    const nextClip = this.callClips[this.currentClipIndex + 1];
    console.log({ nextClip });
    const clip = nextClip.clip;
    const nextClipStartTime = clip?.start;
    const nextClipStopTime = clip?.stop;
    const nextClipStartSeconds = this.convertTimeStringToSeconds(
      nextClipStartTime || ''
    );
    const nextClipStopSeconds = this.convertTimeStringToSeconds(
      nextClipStopTime || ''
    );
    const nextClipSpeechTime = nextClipStopSeconds - nextClipStartSeconds;
    return nextClipSpeechTime * 1000;
  }

  handleSendUserResponse(): void {
    // here pause transcript
    const isConversational = this.controlService?.isConversational();

    if (this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE) {
      if (
        this.callStore.getSpeechTranscript().length > 0 ||
        this.callStore.getSpeechTranscriptPartial().length > 0
      ) {
        console.log(`handleSendUserResponse!`);
        const currentClip = this.callClips[this.currentClipIndex];
        this.finalQuestionUuid = currentClip.questionUuid;

        const isManual =
          this.callStore.getAdvanceType() == AppConstants.ADVANCE_TYPE_MANUAL;
        if (isManual) {
          this.callStore.setIsAvatarThinking(true);
          this.callStore.setIsAvatarSpeaking(false);
          this.isAvatarListening = false;
          let timeoutSendAnswer = 420;
          this.preResponseWasPlayed = false;
          console.log({ timeoutSendAnswer });

          this.callStore.setIsContinueButtonBlocked(true);
          this.callStore.setIsSendingAnswer(true);
          this.callStore.setIsWaitingTimeout(true);
          setTimeout(() => {
            this.assemblyService.forceEndUtterance();
          }, timeoutSendAnswer - 180);
          // added timeout to catch lastest partial transcript from assemblyAI, since it hava an utterance delay
          setTimeout(() => {
            console.log(`timeoutSendAnswer!`);
            this.callStore.setIsWaitingTimeout(false);
            // make sure we are still in practice view
            const isPracticeScreen =
              this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
            const isNotEndCall = !this.controlService?.isEndCallTriggered;
            if (isPracticeScreen && isNotEndCall) {
              this.setFinalAnswer();
              this.callStore.setIsSendingAnswer(true);
              if (isConversational) {
                this.sendAnswerConversational();
              } else {
                this.sendAnswer();
              }
              this.callStore.setSpeechTranscript('');
              this.callStore.setSpeechTranscriptPartial('');
              this.callStore.setRecognizedTranscripts([]);
              this.handleLastResponseSent();
            }
          }, timeoutSendAnswer);
        } else {
          // make sure we are still in practice view
          const isPracticeScreen =
            this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
          const isNotEndCall = !this.controlService?.isEndCallTriggered;
          if (
            isPracticeScreen &&
            isNotEndCall &&
            !this.callStore.getIsSendingAnswer()
          ) {
            this.setFinalAnswer();
            this.callStore.setIsSendingAnswer(true);
            if (isConversational) {
              this.sendAnswerConversational();
            } else {
              this.sendAnswer();
            }
            this.callStore.setSpeechTranscript('');
            this.callStore.setSpeechTranscriptPartial('');
            this.callStore.setRecognizedTranscripts([]);
            this.handleLastResponseSent();
          }
        }
      }
    } else {
      this.callStore.setIsContinueButtonBlocked(false);
    }
    this.userInterruptedAvatar = false;
  }

  async sendVisualAidResponse(
    answer: string,
    currentClipIndex?: number
  ): Promise<void> {
    return new Promise((resolve) => {
      if (this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE) {
        if (answer.length > 0) {
          console.log(`handleSendUserVisualAidResponse!`);

          const currentClip =
            this.callClips[currentClipIndex ?? this.currentClipIndex];
          this.finalQuestionUuid = currentClip.questionUuid;

          const isManual =
            this.callStore.getAdvanceType() == AppConstants.ADVANCE_TYPE_MANUAL;
          if (isManual) {
            this.callStore.setIsAvatarThinking(true);
            this.isAvatarListening = false;
            let timeoutSendAnswer = 420;
            this.preResponseWasPlayed = false;

            setTimeout(() => {
              this.callStore.setIsWaitingTimeout(false);

              const isPracticeScreen =
                this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
              const isNotEndCall = !this.controlService?.isEndCallTriggered;

              if (isPracticeScreen && isNotEndCall) {
                this.callStore.setIsSendingAnswer(true);
                this.sendVisualAidAnswer(answer);
                this.lastUserMessageSent = this.finalAnswer;
                this.callStore.setSpeechTranscript('');
                this.callStore.setSpeechTranscriptPartial('');
                this.callStore.setRecognizedTranscripts([]);
                this.handleLastResponseSent();
              }

              resolve(); // ✅ Done!
            }, timeoutSendAnswer);
          } else {
            const isPracticeScreen =
              this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
            const isNotEndCall = !this.controlService?.isEndCallTriggered;

            if (
              isPracticeScreen &&
              isNotEndCall &&
              !this.callStore.getIsSendingAnswer()
            ) {
              this.callStore.setIsSendingAnswer(true);
              this.sendVisualAidAnswer(answer);
              this.lastUserMessageSent = this.finalAnswer;
              this.callStore.setSpeechTranscript('');
              this.callStore.setSpeechTranscriptPartial('');
              this.callStore.setRecognizedTranscripts([]);
              this.handleLastResponseSent();
            }

            resolve(); // ✅ Resolve immediately for non-manual case
          }
        } else {
          resolve(); // ✅ Nothing to send
        }
      } else {
        this.callStore.setIsContinueButtonBlocked(false);
        resolve(); // ✅ Not practice view
      }
      this.userInterruptedAvatar = false;
    });
  }

  setFinalAnswer(): void {
    this.finalAnswer = this.getAnswer();
    this.lastUserMessageSent = this.finalAnswer;
    // automatic call validation
    const isAuto =
      this.callStore.getAdvanceType() == AppConstants.ADVANCE_TYPE_AUTOMATIC;
    const isConversational = this.controlService?.isConversational();
    if (isAuto && !isConversational) {
      return;
    }
    // call pause transcript service
    this.transcriptCallCheck(true);
  }

  sendAnswer(): void {
    const practiceCallUuid = this.controlService?.practiceCallUuid;
    const questionUuid = this.finalQuestionUuid;
    const answer = this.finalAnswer;
    if (!practiceCallUuid) {
      throw new Error('practiceCallUuid is null');
    }
    if (!questionUuid) {
      throw new Error('questionUuid is null');
    }
    if (!answer) {
      throw new Error(`empty answer for questionUuid [${questionUuid}]`);
    }

    const shouldSend =
      questionUuid != null &&
      answer.length > 0 &&
      questionUuid != this.lastQuestionUuidSent;
    if (shouldSend) {
      console.log(
        `SEND answer to server \n questionUuid [${questionUuid}]\n practiceCallUuid [${this.controlService?.practiceCallUuid}]\n answer %c[${answer}]`,
        'background: #00647B; color: #fff;'
      );
      this.logger.log('[CALL] player - SEND answer to server', {
        questionUuid,
        practiceCallUuid: this.controlService?.practiceCallUuid,
        answer,
      });
      // we save the last questionUuid sent to prevent sending the same questionUuid twice
      this.lastQuestionUuidSent = questionUuid;
      this.maintainAgentChatHistory();
      this.maintainChatHistory('userSays', answer);
      this.callStore.setAnswerData(null);
      this.callStore.setWasSendingAnswer(false);
      this.callService
        .sendUserAnswer(practiceCallUuid, questionUuid, answer)
        .subscribe({
          next: () => {
            this.finalAnswer = '';
          },
          error: (err) => {
            this.logger.error('[CALL] player - error sending answer', err);
            // if offline on guided call, save the answer to send later
            if (!navigator.onLine) {
              this.callStore.setWasSendingAnswer(true);
              this.callStore.setAnswerData({
                practiceCallUuid,
                questionUuid,
                answer,
              });
              this.logger.error(
                '[CALL] player - Error sending answer, you are offline!'
              );
            }
          },
          complete: () => {
            console.log(`sendUserAnswer to questionUuid [${questionUuid}]`);
            this.callStore.setIsContinueButtonBlocked(true);
            this.callStore.setIsSendingAnswer(false);
          },
        });
    } else {
      this.callStore.setIsContinueButtonBlocked(false);
    }
  }

  sendVisualAidAnswer(answer: string): void {
    const practiceCallUuid = this.controlService?.practiceCallUuid;
    const questionUuid = this.finalQuestionUuid;
    if (!practiceCallUuid) {
      throw new Error('practiceCallUuid is null');
    }
    if (!questionUuid) {
      throw new Error('questionUuid is null');
    }
    if (!answer) {
      throw new Error(`empty answer for questionUuid [${questionUuid}]`);
    }

    const shouldSend =
      questionUuid != null &&
      answer.length > 0 &&
      questionUuid != this.lastQuestionUuidSent;
    if (shouldSend) {
      console.log(
        `SEND answer to server \n questionUuid [${questionUuid}]\n practiceCallUuid [${this.controlService?.practiceCallUuid}]\n answer %c[${answer}]`,
        'background: #00647B; color: #fff;'
      );
      this.logger.log('[CALL] player - SEND answer to server', {
        questionUuid,
        practiceCallUuid: this.controlService?.practiceCallUuid,
        answer,
      });
      // we save the last questionUuid sent to prevent sending the same questionUuid twice
      this.lastQuestionUuidSent = questionUuid;
      this.maintainAgentChatHistory();
      this.maintainChatHistory('userSays', answer);
      this.callStore.setAnswerData(null);
      this.callStore.setWasSendingAnswer(false);
      this.callService
        .sendUserAnswer(practiceCallUuid, questionUuid, answer)
        .subscribe({
          next: () => {
            this.clearTranscripts();
          },
          error: (err) => {
            this.logger.error('[CALL] player - error', err);
            // if offline on guided call, save the answer to send later
            if (!navigator.onLine) {
              this.callStore.setWasSendingAnswer(true);
              this.callStore.setAnswerData({
                practiceCallUuid,
                questionUuid,
                answer,
              });
              this.logger.error(
                '[CALL] player - Error sending answer, you are offline!'
              );
            }
          },
          complete: () => {
            console.log(`sendUserAnswer to questionUuid [${questionUuid}]`);
            this.callStore.setIsContinueButtonBlocked(true);
            this.callStore.setIsSendingAnswer(false);
          },
        });
    } else {
      this.callStore.setIsContinueButtonBlocked(false);
    }
  }

  maintainAgentChatHistory(timedOutIndex: number = 1): void {
    const clip: ICallClip =
      this.callClips[this.currentClipIndex - timedOutIndex];
    if (clip) {
      // clip
      const callClip: IClip | null = clip.clip;
      this.maintainChatHistory('agentSays', callClip?.agentSays || '');
    }
  }

  maintainChatHistory(speaker: string, speech: string): void {
    let convoHistory: { chatHistory: string[] } = {
      chatHistory: [],
    };

    if (localStorage.getItem('convoHistory')) {
      convoHistory = JSON.parse(localStorage.getItem('convoHistory') || '') as {
        chatHistory: string[];
      };
    }
    convoHistory.chatHistory.push(`${speaker}: ${speech}`);
    localStorage.setItem('convoHistory', JSON.stringify(convoHistory));
    this.chatHistory = JSON.stringify(convoHistory);
  }

  handleLastResponseSent(): void {
    const isConversational = this.controlService?.isConversational();
    if (isConversational) {
      return;
    }
    // if we are on manual call we don't add 1 because of setTimout that moves index to next clip
    const isManual =
      this.callStore.getAdvanceType() == AppConstants.ADVANCE_TYPE_MANUAL;
    const add = isManual ? 0 : 1;
    const index = this.currentClipIndex + add;
    const length = this.callClips.length;

    // if is the last clip, then call is complete
    if (index >= length) {
      this.callStore.setIsCallComplete(true);
      this.shouldHangup = true;
      return;
    }

    // search for next clip with question, if not found then call is complete
    let foundQuestionUuid = false;
    for (let i = index; i < length; i++) {
      if (this.callClips[i].questionUuid != null) {
        foundQuestionUuid = true;
        break;
      }
    }
    // call will be complete if no questionUuid pending was found
    this.callStore.setIsCallComplete(!foundQuestionUuid);
    this.shouldHangup = !foundQuestionUuid;
  }

  handleHighlightHangup(): void {
    const isConversational = this.controlService?.isConversational();
    const clipIndex = this.currentClipIndex + 1 == this.callClips.length;
    if (clipIndex && !isConversational) {
      // if the last clip is not a question we can auto hangup
      if (this.callClips[this.currentClipIndex].questionUuid == null) {
        this.shouldHangup = true;
        this.callStore.setIsCallComplete(true);
      }
    }
  }

  async transcriptCallCheck(pause: boolean = false): Promise<void> {
    const isPracticeView =
      this.callStore.getCurrentView() == AppConstants.VIEW_PRACTICE;
    if (isPracticeView) {
      if (pause) {
        await this.callPauseTranscript(pause);
      } else {
        await this.callResumeTranscript(pause);
      }
      return;
    }
  }

  async callPauseTranscript(pause: boolean = true): Promise<void> {
    const isPaused = this.callStore.getIsPaused();
    const isMicOff = !this.callStore.getIsMicOn();
    if (isPaused || pause || isMicOff) {
      await this.assemblyService.pause();
      return;
    }

    const isManual =
      this.callStore.getAdvanceType() == AppConstants.ADVANCE_TYPE_MANUAL;
    const isAvatarSpeaking = this.callStore.getIsAvatarSpeaking();
    const isLoopActive = this.isLoopActive;
    if (isManual) {
      if (isAvatarSpeaking || !isLoopActive) {
        await this.assemblyService.pause();
        return;
      }
    }

    const isAuto =
      this.callStore.getAdvanceType() == AppConstants.ADVANCE_TYPE_AUTOMATIC;
    if (isAuto) {
      const isConversational = this.controlService?.isConversational();
      // on loop inactive, only if not conversational
      if (!isLoopActive && !isConversational) {
        this.handleTimeWindowToInterruptAvatarSpeech();
        return;
      }
    }
  }

  async callResumeTranscript(pause: boolean = true): Promise<void> {
    const isPaused = this.callStore.getIsPaused();
    const isMicOn = this.callStore.getIsMicOn();
    if (!isPaused && !pause && isMicOn) {
      const isManual =
        this.callStore.getAdvanceType() == AppConstants.ADVANCE_TYPE_MANUAL;
      const isAvatarSpeaking = this.callStore.getIsAvatarSpeaking();
      if (isManual && isAvatarSpeaking) {
        return;
      }

      const isConversational = this.controlService?.isConversational();
      if (isConversational && this.callStore.getIsSendingAnswer()) {
        return;
      }

      const indexValidation = this.currentClipIndex < this.callClips.length - 1;
      if (isConversational && indexValidation) {
        return;
      }

      const isLoopActive = this.isLoopActive;
      if (isConversational && !isLoopActive) {
        return;
      }

      if (
        !isConversational &&
        !isLoopActive &&
        this.timeToInterruptAvatarPassed &&
        !this.isFirstLoadPracticeView
      ) {
        return;
      }
      await this.assemblyService.resume();
      return;
    }
  }

  async handleUserStopSpeaking(): Promise<void> {
    const isSpeech = this.callStore.getSpeechTranscript().length > 0;
    const isSpeechPartial =
      this.callStore.getSpeechTranscriptPartial().length > 0;
    // if user had said something
    if (isSpeech || isSpeechPartial) {
      const lastMessageTimeStamp = this.callStore
        .getLastMessageTimeStamp()
        .getTime();
      const timeSinceLastMessage = new Date().getTime() - lastMessageTimeStamp;
      // time validation user no speech
      if (timeSinceLastMessage >= this.waitTimeNoSpeechToSendResponse) {
        console.log(`handleUserStopSpeaking!`);
        if (this.timeToInterruptAvatarPassed) {
          await this.assemblyService.pause();
        }
        this.nextClip();
      }
    }
  }

  async handleTimeWindowToInterruptAvatarSpeech(): Promise<void> {
    const clip = this.callClips[this.currentClipIndex];
    if (!clip) {
      console.log(
        `Next clip is null on handleTimeWindowToInterruptAvatarSpeech`
      );
      return;
    }
    const isCoachClip = clip.type == AppConstants.VIEW_PRE_CALL;
    if (
      !isCoachClip &&
      !this.userInterruptedAvatar &&
      !this.timeToInterruptAvatarPassed
    ) {
      const msgTimeStamp = this.callStore.getLastMessageTimeStamp().getTime();
      const timeSinceLastMessage = new Date().getTime() - msgTimeStamp;
      console.log(
        `timeSinceLastMessage: ${timeSinceLastMessage} ms limit: ${this.waitTimeUserInterruptAvatarSpeech} ms`
      );
      // time to interrupt avatar passed
      if (timeSinceLastMessage >= this.waitTimeUserInterruptAvatarSpeech) {
        console.log('time to interrupt avatar passed!');
        this.timeToInterruptAvatarPassed = true;
        await this.assemblyService.pause();
        if (this.finalQuestionUuid) {
          this.sendAnswer();
        }
      } else {
        this.handleUserInterruptedAvatarSpeech(timeSinceLastMessage);
      }
    }
  }

  async handleUserInterruptedAvatarSpeech(
    timeSinceLastMessage: number
  ): Promise<void> {
    const isTranscript = this.callStore.getSpeechTranscript().length > 0;
    const isPartialTranscript = this.callStore.getPartialTranscriptExists();
    const isUserSpeaking = isTranscript || isPartialTranscript;
    const isConversational = this.controlService?.isConversational();
    // if user has sent a message before and had said something
    if (
      this.lastUserMessageSent != null &&
      isUserSpeaking &&
      !isConversational
    ) {
      // time validation user interrupt avatar speech
      if (timeSinceLastMessage < this.waitTimeUserInterruptAvatarSpeech) {
        console.log('userInterruptedAvatar!');
        this.userInterruptedAvatar = true;
        this.currentClipIndex = this.currentClipIndex - 1;
        this.setCurrentIndexClipData();
        this.logger.log('[CALL] player - handleUserInterruptedAvatarSpeech');
        await this.assemblyService.resume();
      }
    }
  }

  setCallClipsHTML(): void {
    // set unique filenames to practiceCallClips for html video elements
    if (this.callClips.length > 0) {
      let index = this.practiceCallClips.length;
      // get unique filenames
      this.callClips.forEach((callClip: ICallClip) => {
        // push clip filename only if not exists
        const file = callClip.clip?.filename || '';
        index = this.addCallClip(file, index);

        // push clip loop filename only if not exists
        const loopFile = callClip.clipLoop?.filename || '';
        index = this.addCallClip(loopFile, index);
      });
      this.initPracticeCallClipsLength = this.practiceCallClips.length;
      console.log({ practiceCallClips: this.practiceCallClips });
    }
  }

  getPreResponseClips(): void {
    let index = this.practiceCallClips.length;
    // Get the filename from PRERESPONSE type
    if (this.controlService?.callConversational) {
      this.controlService.callConversational.preResponseClips.forEach(
        (callClip: IClip) => {
          index = this.addCallClip(callClip.filename, index);
        }
      );
      console.log(`Preresponse clips: `, this.practiceCallClips);
    }
  }

  addCallClip(src: string, index: number): number {
    // validate empty src
    if (src.length == 0) {
      return index;
    }
    // validate if src already exists
    const className = 'd-none';
    const clipExist = this.practiceCallClips.some((obj) => obj.src === src);
    if (!clipExist) {
      this.practiceCallClips.push({ index, src, className });
      index++;
    } else {
      const index2 = this.practiceCallClips.findIndex((obj) => obj.src === src);
      // remove classes from existing clip
      this.practiceCallClips[index2].className = className;
    }
    return index;
  }

  setVideoHTML(): void {
    const id = `avatar-video-${this.currentVideoClipIndex}`;
    // set videoHTMLElement to current clip to be played
    this.videoHTMLElement = document.getElementById(id) as HTMLVideoElement;
  }

  getVideoHeight(): void {
    // fn only required on visual aid call - TODO: delete when not needed
    if (
      !this.controlService?.isVisualAidCall ||
      (this.controlService?.isVisualAidCall &&
        this.controlService.realtimeService)
    ) {
      return;
    }
    const isSmall =
      this.avatarOffsetHeight === 150 && this.avatarOffsetWidth === 300;
    const isZero = this.avatarOffsetHeight <= 0 || this.avatarOffsetWidth <= 0;
    if (isSmall || isZero) {
      setTimeout(() => {
        const index = this.currentVideoClipIndex || 0;
        const id = `avatar-video-${index}`;
        const avatarVideo = document.getElementById(id) as HTMLVideoElement;
        if (avatarVideo) {
          const height = avatarVideo.offsetHeight;
          this.setHeightElement('user-container', height);
          this.setHeightElement('video-content', height);
          this.setHeightElement('videos', height);

          const width = avatarVideo.offsetWidth;
          this.setHeightElement('avatar-video', height, width);
          this.setHeightElement('video-top-elements', height, width);
          this.setHeightElement('video-background', height, width);
          this.avatarOffsetHeight = height;
          this.avatarOffsetWidth = width;
        }

        if (this.avatarOffsetHeight === 150 && this.avatarOffsetWidth === 300) {
          this.setVideoSize();
        }
      }, 0);
    }
  }

  setHeightElement(id: string, height: number, width: number = 0): void {
    const htmlElement = document.getElementById(id);
    if (htmlElement && height > 0) {
      htmlElement.style.height = `${height}px`;
      htmlElement.style.maxHeight = `${height}px`;
    }
    if (htmlElement && width > 0) {
      htmlElement.style.width = `${width}px`;
      htmlElement.style.maxWidth = `${width}px`;
    }
  }

  hideAllAvatarVideos(except?: string): void {
    this.practiceCallClips.forEach((obj) => {
      if (!obj.className.includes('d-none') && obj.src != except) {
        obj.className += ' d-none';
      }
    });
  }

  pauseAllVideos(): void {
    this.practiceCallClips.forEach((obj) => {
      const video = document.getElementById(
        `avatar-video-${obj.index}`
      ) as HTMLVideoElement;
      if (video && !video.paused) {
        video.pause();
      }
    });
  }

  sendAnswerConversational(
    handleResponse: boolean = true,
    saveChat: boolean = true
  ): void {
    this.controlService?.addInteraction();
    const callUuid = this.callStore.getCall()?.callUuid || null;
    const practiceCallUuid = this.callStore.getCall()?.practiceCallUuid || null;
    const answer = this.finalAnswer;

    if (!callUuid) {
      throw new Error('callUuid is null');
    }
    if (!practiceCallUuid) {
      throw new Error('practiceCallUuid is null');
    }
    if (!answer) {
      throw new Error(`empty answer for callUuid [${callUuid}]`);
    }

    console.log(
      `SEND answer to server \n callUuid [${callUuid}]\n answer %c[${answer}]`,
      'background: #00647B; color: #fff;'
    );

    this.callStore.setIsSendingAnswer(true);
    if (saveChat) {
      this.maintainAgentChatHistory();
    }

    this.isWaitingConversationalResponse = true;
    const topClips = this.controlService?.topClips;

    this.answerSentDateTime = new Date();
    this.conversationStatusCode = 0;
    this.startConversationalInterval();

    this.callService
      .sendUserAnswerConversational(
        callUuid,
        practiceCallUuid,
        answer,
        topClips
      )
      .subscribe({
        next: (res: IConversational) => {
          this.conversationStatusCode = 200;
          this.finalAnswer = '';
          const isAlert = ['thinking', 'retrying', 'unavailable'].includes(
            this.converseStatus || ''
          );
          if (this.controlService && isAlert) {
            this.converseStatus = 'ready';
            this.shouldHandleResponse = handleResponse ? res : null;
          } else {
            this.converseStatus = null;
            if (handleResponse) {
              this.handleConversationalResponse(res);
            }
          }
        },
        error: (err) => {
          this.conversationStatusCode = 500;
          this.logger.error('[CALL] player - error', err);
          this.isWaitingConversationalResponse = false;
        },
        complete: () => {
          this.callStore.setIsContinueButtonBlocked(true);
        },
      });

    if (saveChat) {
      this.maintainChatHistory('userSays', answer);
    }

    // remove previous clips to make space for new clips from conversational
    if (this.practiceCallClips.length > this.initPracticeCallClipsLength + 2) {
      this.practiceCallClips.splice(
        this.initPracticeCallClipsLength,
        this.practiceCallClips.length - 1
      );
    }
  }

  handleConversationalResponse(conv: IConversational) {
    this.shouldHandleResponse = null;
    const isPracticeScreen =
      this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
    if (!isPracticeScreen) {
      return;
    }

    this.callStore.setSpeechTranscript('');
    this.callStore.setSpeechTranscriptPartial('');
    this.callStore.setIsContinueButtonBlocked(true);

    if (this.isPlayingPreResponse) {
      setTimeout(() => {
        this.handleConversationalResponse(conv);
      }, 100);
      return;
    }
    if (conv.videoClips) {
      const clips = conv.videoClips;
      let index = this.practiceCallClips.length;

      // get agent says from conversational response
      let agentSays = '';
      clips.forEach((clip: IClip, i) => {
        agentSays += clip.agentSays + ' ';
      });

      clips.forEach((clip: IClip, i) => {
        // set converse clips in html, push clip filename only if not exists
        const file = clip.filename;
        index = this.addCallClip(file, index);
        clip.clipSays = clip.agentSays;
        clip.agentSays = agentSays;

        // set clips for player
        const loop = this.controlService?.callConversational?.clipLoop || null;
        const isLastClip = i == clips.length - 1;
        const avatarName =
          this.controlService?.callConversational?.avatarName ||
          this.avatarName;
        const hints =
          this.controlService?.callConversational?.hints ||
          this.callStore.getHints();
        const clipLoop = isLastClip ? loop : null;
        const callClip: ICallClip = {
          type: AppConstants.VIEW_PRACTICE,
          avatarName,
          hints,
          questionUuid: null,
          clip,
          clipLoop,
        };
        this.callClips.push(callClip);
      });

      // start play clips
      this.callStore.setIsContinueButtonBlocked(true);

      let preResponseClips =
        this.controlService?.callConversational?.preResponseClips;
      if (preResponseClips && preResponseClips.length != 0) {
        this.currentClipIndex++;
      }

      if (this.currentClipIndex < this.callClips.length) {
        this.setCurrentIndexClipData();
      }
      this.callStore.setIsAvatarThinking(false);
      this.isWaitingConversationalResponse = false;
    }
    this.callStore.setIsSendingAnswer(false);
  }

  joinAgentSays(callClips: ICallClip[]): void {
    let lastIndex = 0;
    for (let i = 0; i < callClips.length; i++) {
      let callClip = callClips[i];
      if (callClip.clipLoop != null) {
        let idx = i + 1;
        let slice = callClips.slice(lastIndex, idx);

        let text = '';
        slice.forEach((clip) => {
          text += clip.clip?.agentSays + ' ';
        });
        for (let j = lastIndex; j < idx; j++) {
          if (callClips[j].clip != null) {
            let clipOld: IClip = callClips[j].clip || {
              filename: '',
              start: '',
              stop: '',
              agentSays: '',
            };
            let clipNew: IClip = {
              ...clipOld,
              agentSays: text,
              clipSays: clipOld.agentSays,
            };
            let callNew: ICallClip = {
              ...callClips[j],
              clip: clipNew,
            };

            callClips[j] = callNew;
          }
          lastIndex = idx;
        }
      }
    }
    this.callClips = callClips;
  }

  validateCallComplete(): boolean {
    const isConversational = this.controlService?.isConversational();
    if (!isConversational) {
      const remainingClips = this.callClips.slice(this.currentClipIndex);
      // validate if remaining clips doesn't have questionUuid
      const isCallComplete = remainingClips.every(
        (clip) => clip.questionUuid == null
      );
      return isCallComplete;
    }
    return false;
  }

  calculateElapsedTime(date: Date): number {
    const now = new Date();
    const time = now.getTime() - date.getTime();
    return time;
  }

  startConversationalInterval(): void {
    clearInterval(this.conversationalInterval);
    this.conversationalInterval = setInterval(() => {
      // Conversation success
      if (this.conversationStatusCode === 200) {
        clearInterval(this.conversationalInterval);
        this.callStore.setIsLoadingNoResponse(false);
        this.converseStatus = 'ready';
        this.answerSentDateTime = null;
        this.logger.error(
          '[CALL] player - error',
          `Slow conversation response, displaying message [ready] request code [${this.conversationStatusCode}]`
        );
        return;
      }

      // Calculate elapsed passed since answer sent
      let elapsedTime = 0;
      if (this.answerSentDateTime) {
        elapsedTime = this.calculateElapsedTime(this.answerSentDateTime);
      }

      // Conversation error or timeout after 5 seconds
      if (elapsedTime > 5000) {
        console.log(`Conversation retry error or timeout after 5 seconds`);
        clearInterval(this.conversationalInterval);
        this.callStore.setIsLoadingNoResponse(false);
        this.callStore.setOpenNoResponseModal(true);
        if (this.converseStatus === 'retrying') {
          this.converseStatus = 'unavailable';
          this.logger.error(
            '[CALL] player - error',
            `Slow conversation response, displaying message [unavailable] request code [${this.conversationStatusCode}]`
          );
        } else {
          this.converseStatus = 'thinking';
          this.logger.error(
            '[CALL] player - error',
            `Slow conversation response, displaying message [thinking] request code [${this.conversationStatusCode}]`
          );
        }
      }

      if (this.callStore.getCurrentView() !== AppConstants.VIEW_PRACTICE) {
        console.log(
          `Conversation retry error or timeout after 5 seconds, currentView ${this.callStore.getCurrentView()} is diff practice`
        );
        clearInterval(this.conversationalInterval);
        this.converseStatus = null;
        this.shouldHandleResponse = null;
        this.callStore.setIsLoadingNoResponse(true);
        this.callStore.setOpenNoResponseModal(false);
      }
    }, 250);
  }

  addCanPlayFlag(video: HTMLVideoElement): void {
    // Add the event listener
    video.addEventListener('canplay', () => {
      // when video is ready to play, we add the canplay class and set canPlay flag to true
      this.addClassToClip('canplay', video.src);
      if (video.classList.contains('triggerCanPlay')) {
        this.removeClassFromClip('triggerCanPlay', video.src);
        this.triggerCanPlay(this.triggerIsLoop, this.triggerStart);
      }
    });

    video.addEventListener('play', () => {
      const isPracticeView =
        this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
      if (this.showLoadingScreen && isPracticeView) {
        this.showLoadingScreen = false;
        setTimeout(() => {
          this.userVideoService.displayUserVideo();
        }, 500);
      }
      // unhide current video
      this.removeClassFromClip('d-none', video.src);

      if (isPracticeView) {
        this.canPlayVideo = true;
      }

      if (isPracticeView && video.offsetWidth > 0) {
        this.setVideoSize();
      }

      // hide loop clip
      if (isPracticeView && !this.isLoopActive) {
        const isLoopDiffCurrent = this.loopClipFilename != video.src;
        if (this.loopClipFilename && isLoopDiffCurrent) {
          this.addClassToClip('d-none', this.loopClipFilename);
        }
      }
    });

    video.addEventListener('loadedmetadata', () => {
      const isPracticeView =
        this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
      if (isPracticeView && video.offsetWidth > 0) {
        this.setVideoSize();
      }
    });
  }

  addClassToClip(newClass: string, src: string): void {
    const index = this.practiceCallClips.findIndex((clip) => clip.src === src);
    if (index !== -1) {
      if (!this.practiceCallClips[index].className.includes(newClass)) {
        this.practiceCallClips[index].className += ` ${newClass}`;
      }
    }

    // add class to video element
    const video = document.getElementById(src) as HTMLVideoElement;
    if (video) {
      video.classList.add(newClass);
    }
  }

  removeClassFromClip(oldClass: string, src: string): void {
    const index = this.practiceCallClips.findIndex((clip) => clip.src === src);
    if (index !== -1) {
      this.practiceCallClips[index].className = this.practiceCallClips[
        index
      ].className.replace(oldClass, '');
    }

    // remove class from video element
    const video = document.getElementById(src) as HTMLVideoElement;
    if (video) {
      video.classList.remove(oldClass);
    }
  }

  handleSendUserResponseVisualAid(page: string) {
    setTimeout(() => {
      const currentTranscript = this.getAnswer();
      if (currentTranscript && currentTranscript.trim().length > 0) {
        const answer = page + currentTranscript + '\n';
        this.callStore.setRecognizedTranscripts([answer]);
      }
      console.log('Visual Aid Answer on ' + page + ':\n', currentTranscript);
    }, 1000);
  }

  loadVideoCurrentTime(id: string): void {
    const video = document.getElementById(id) as HTMLVideoElement;
    if (video) {
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = this.loopClipStartTime;
      });
    }
  }

  isClipValid(clip: IClip): boolean {
    if (!this.controlService?.isVisualAidCall) return true;
    if (
      !clip.filename.trim() ||
      !clip.agentSays.trim() ||
      !clip.start.trim() ||
      !clip.stop.trim()
    ) {
      return false;
    }
    // If clipSays is present, check if it’s non-empty after trimming
    if (clip.clipSays !== undefined && clip.clipSays.trim() === '') {
      return false;
    }
    return true;
  }

  async initAssembly(): Promise<void> {
    this.assemblyService.init();
    this.assemblyService.startTranscriber(false);
  }

  async pauseAssembly(): Promise<void> {
    this.assemblyService.pause();
  }

  async stopAssembly(): Promise<void> {
    await this.assemblyService.stop();
  }

  async restartTranscriber(startRecording: boolean = true): Promise<void> {
    this.assemblyService.restartTranscriber(startRecording);
  }

  getAnswer(): string {
    // Force end the current utterance
    this.assemblyService.forceEndUtterance();

    // Get partial transcript and check if it is already recognized
    let partialTranscript = this.callStore.getSpeechTranscriptPartial();
    const includePartial = this.callStore
      .getRecognizedTranscripts()
      .includes(partialTranscript);
    if (!includePartial && partialTranscript.length > 0) {
      // Handle call words and replace words
      const callWords = this.callStore.getCallWords();
      const replaceWords = this.callStore.getReplaceWords();
      if (callWords.length > 0 && replaceWords.length > 0) {
        const correctedPartialTranscript: correctedTranscript =
          correctTranscript(partialTranscript, callWords, replaceWords);
        this.logger.log('[CALL] player - conversation speech', {
          originalText: partialTranscript,
          correctedText: correctedPartialTranscript.transcript,
        });
        // Update the partial transcript with the corrected one
        partialTranscript = correctedPartialTranscript.transcript;
        this.callStore.addTranscriptChangesLogs(
          correctedPartialTranscript.changes
        );
      }
      // Add the partial transcript to the recognized transcripts
      this.callStore.setRecognizedTranscripts([
        ...this.callStore.getRecognizedTranscripts(),
        partialTranscript,
      ]);
    }

    // Get the full transcript
    const transcript = this.callStore.getRecognizedTranscripts().join(' ');
    this.logger.log(`[CALL] player - get answer [${transcript}]`);
    this.clearTranscripts();
    return transcript;
  }

  clearTranscripts(): void {
    this.callStore.setSpeechTranscript('');
    this.callStore.setRecognizedTranscripts([]);
    this.callStore.setSpeechTranscriptPartial('');
  }
}
